import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import RaitingButton from '../../components/buttons/rating';
import BottomModal from '../../components/modal/bottomModal';
import { useBackPress } from '../../hooks/useBackPress';
import TextInput, { inputType } from '../../components/inputs/textInput';
import ConfirmButton from '../../components/buttons/confirmButton';

const PaymentRating = forwardRef(
  ({ orderRating, payment, restaurantName, reviewLink, onUpdateRating, fromHome }, ref) => {
    const { t } = useTranslation();
    const { pathname } = useLocation();

    let ratingToUse;
    if (orderRating) {
      ratingToUse = orderRating;
    } else if (payment && payment.rating) {
      ratingToUse = payment.rating;
    }

    const [ratingModalVisible, setRatingModalVisible] = useState(false);
    const [displayRating, setDisplayRating] = useState(ratingToUse ? ratingToUse.rating || 0 : 0);

    const [editRating, setEditRating] = useState(ratingToUse ? ratingToUse.rating || 0 : 0);
    const [comment, setComment] = useState(ratingToUse ? ratingToUse.comment || '' : '');

    const hasExternalReview = !!reviewLink;

    useImperativeHandle(ref, () => ({
      openReviewForm() {
        openReviewForm();
      },
    }));

    const { handleBackOnModalOpen, handleBackOnModalClose } = useBackPress(
      fromHome ? '/' : pathname,
      () => {
        setRatingModalVisible(false);
      },
    );

    const onReviewConfirm = (newRating, newComment) => {
      setDisplayRating(newRating);
      onUpdateRating(newRating, newComment);
      setRatingModalVisible(false);

      if (fromHome) {
        setTimeout(() => {
          setDisplayRating(0);
          setEditRating(0);
          setComment('');
        }, 1000);
      }
    };

    const renderRatingEdit = () => {
      return (
        <div>
          <RaitingButton
            rating={editRating}
            onChange={(val) => {
              setEditRating(val);
            }}
          />
          <div className="mt-7" />
          <TextInput
            id="review-comment"
            onChange={setComment}
            value={comment}
            type={inputType.longText}
            placeholder={t('payment.leaveComment')}
          />
          <ConfirmButton
            onClick={() => {
              onReviewConfirm(editRating, comment);
            }}
            disabled={!editRating}
          />
        </div>
      );
    };

    const openReviewForm = () => {
      if (hasExternalReview) {
        global.window.open(reviewLink, '_blank');
      } else {
        setRatingModalVisible(true);
        handleBackOnModalOpen();
      }
    };

    return (
      <>
        {!fromHome && (
          <div className="flex justify-center">
            <div
              className="bg-successLight rounded-3xl px-8 sm:px-20 py-8 mt-5 flex justify-center items-center flex-col max-w-xl"
              role="button"
              onClick={() => {
                openReviewForm();
              }}
            >
              <div className="font-primaryMedium text-success text-xl text-center mb-2 cursor-pointer">
                {t('payment.rateYourExperience', {
                  restaurantName: restaurantName || '',
                })}
              </div>
              <div
                role="button"
                onClick={() => {
                  openReviewForm();
                }}
              >
                {!hasExternalReview && <RaitingButton rating={displayRating} disabled />}
                {hasExternalReview && (
                  <div className="font-primaryMedium text-base underline mt-3">
                    {t('buttons.leaveReview')}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <BottomModal
          title={t('payment.reviewTitle')}
          content={renderRatingEdit()}
          onClose={() => {
            setRatingModalVisible(false);
            handleBackOnModalClose();
          }}
          onOutClick={() => {
            setRatingModalVisible(false);
            handleBackOnModalClose();
          }}
          open={ratingModalVisible}
        />
      </>
    );
  },
);

export default PaymentRating;
